import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import useAlert from '~/components/useAlert'

const UseAlertPage: Component = () => {
  const alert = useAlert()

  const triggerComplexAlert = () => {
    alert.show({
      type: 'info',
      icon: 'material-symbols-light:rocket-launch-outline',
      title: 'Despliegue en Progreso',
      subtitle: 'Instancia: production-v2',
      message: 'Estamos migrando los servicios de base de datos a la nueva infraestructura.',
      body: (
        <div class="mt-4 p-4 bg-primary-soft rounded-2xl border border-primary/25 border-dashed">
          <div class="flex items-center justify-between mb-2">
            <span class="text-[10px] font-bold text-primary-600 uppercase">Progreso Global</span>
            <span class="text-[10px] font-mono text-primary-500">74%</span>
          </div>
          <div class="h-1.5 w-full bg-primary-soft rounded-full overflow-hidden">
            <div class="h-full bg-primary-500 w-3/4 animate-pulse"></div>
          </div>
        </div>
      ),
      actions: [
        { label: 'Ver Logs', variant: 'text', color: 'primary' },
        { label: 'Entendido', variant: 'contained', color: 'primary' }
      ]
    })
  }

  return (
    <>
      <Title>useAlert - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">useAlert</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Sistema de orquestación de diálogos. El hook useAlert gestiona alertas, confirmaciones y ventanas modales imperativas, desacoplando la lógica de negocio de la presentación visual.
          </p>
        </div>

        <div class="space-y-12">
          {/* Alertas de Estado */}
          <Card>
            <Card.Header
              title="Notificaciones Imperativas"
              subtitle="Disparadores rápidos para estados de éxito, error, advertencia e información."
              icon={<Icon icon="material-symbols-light:notifications-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Button color="success" variant="soft" onClick={() => alert.success('¡Éxito!', 'Nivel completado.')} icon="material-symbols-light:check-circle-outline" />
                <Button color="danger" variant="soft" onClick={() => alert.error('Fallo Crítico', 'Acceso denegado.')} icon="material-symbols-light:warning-outline" />
                <Button color="warning" variant="soft" onClick={() => alert.warning('Pendiente', 'Revisa tu buzón.')} icon="material-symbols-light:warning-outline" />
                <Button color="info" variant="soft" onClick={() => alert.info('Dato', 'Nueva actualización.')} icon="material-symbols-light:info-outline" />
              </div>
            </Card.Body>
          </Card>

          {/* Promisified Confirmations */}
          <Card>
            <Card.Header
              title="Flujos de Confirmación"
              subtitle="Diálogos asíncronos que retornan promesas para una lógica lineal limpia."
              icon={<Icon icon="material-symbols-light:help-outline" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="flex flex-wrap gap-4 p-8 bg-surface-muted rounded-3xl border border-border">
                <Button
                  color="primary"
                  onClick={async () => {
                    const ok = await alert.confirm({
                      title: '¿Confirmar Envío?',
                      message: 'Esta acción notificará a todos los stakeholders.'
                    })
                    if (ok) alert.success('Procesado')
                  }}
                >
                  Confirmación Simple
                </Button>
                <Button
                  color="danger"
                  variant="outlined"
                  onClick={async () => {
                    const ok = await alert.confirmDelete({
                      title: 'Eliminar Repositorio',
                      message: 'Se perderá todo el historial de commits.'
                    })
                    if (ok) alert.success('Repositorio Purgeado')
                  }}
                >
                  Confirmar Eliminación
                </Button>
                <Button color="secondary" variant="soft" onClick={triggerComplexAlert}>
                  Alerta con Contenido JSX
                </Button>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="Hook Interface"
              subtitle="Métodos y firmas disponibles en la instancia del hook."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Método</th>
                      <th class="px-6 py-4 border-b">Retorno</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">success(title, msg?)</td>
                      <td class="px-6 py-4 italic text-xs">void</td>
                      <td class="px-6 py-4 italic">Cierre automático en 3s.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">error(title, msg?)</td>
                      <td class="px-6 py-4 italic text-xs">void</td>
                      <td class="px-6 py-4">Persistente hasta interacción manual.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-secondary-600">confirm(options)</td>
                      <td class="px-6 py-4 italic text-xs">Promise&lt;boolean&gt;</td>
                      <td class="px-6 py-4 italic">Espera respuesta del usuario.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">show(options)</td>
                      <td class="px-6 py-4 italic text-xs">void</td>
                      <td class="px-6 py-4 italic">Alerta de configuración total (JSX/Actions).</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="mt-6 p-4 bg-warning-soft rounded-2xl border border-warning/25 flex items-start gap-3">
                <Icon icon="material-symbols-light:warning-outline" class="text-amber-500 mt-0.5" size={18} />
                <p class="text-xs text-on-warning-soft leading-relaxed font-medium">
                  <b>RESTRICCIÓN:</b> Es obligatorio renderizar <code class="bg-warning-soft px-1 rounded">{'<alert.AlertComponent />'}</code> en el árbol de componentes donde se invoca el hook para
                  visualizar los diálogos.
                </p>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>

      {/* Render del Hook */}
      <alert.AlertComponent />
    </>
  )
}

export default UseAlertPage
